import React, { useCallback, useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const TAB_FILTERS = [
  { id: 'pending', label: 'Pending review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

const BUSINESS_TYPE_LABEL = {
  funeral_home: 'Funeral home',
  cemetery: 'Cemetery',
  other: 'Other',
};

export default function AdminProfessionalApplications() {
  const [filter, setFilter] = useState('pending');
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvedPin, setApprovedPin] = useState(null);  // {id, pin}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/professional/applications?status=${filter}`);
      setApps(data.applications || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (app) => {
    if (!window.confirm(`Approve ${app.business_name}? An access PIN will be emailed to ${app.email}.`)) return;
    try {
      const { data } = await api.post(`/admin/professional/applications/${app.application_id}/approve`);
      // In dev envs the backend returns the plain PIN once so admins can
      // copy/paste manually while SendGrid keys are pending. In production
      // the PIN is omitted and the email is the single delivery channel.
      if (data?.access_pin) setApprovedPin({ id: app.application_id, pin: data.access_pin });
      toast.success('Professional approved successfully — access code emailed.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (app) => {
    const reason = window.prompt(`Reject ${app.business_name}? Reason (optional):`, '');
    if (reason === null) return;
    try {
      await api.post(`/admin/professional/applications/${app.application_id}/reject`, { reason });
      toast.success('Application rejected');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <div className="space-y-6" data-testid="admin-professional-applications">
        <div>
          <div className="ml-overline">Platform admin</div>
          <h1 className="font-serif text-4xl mt-1 text-[hsl(var(--primary))]">Professional applications</h1>
          <p className="mt-2 text-sm text-[hsl(var(--primary)/0.7)] font-medium">
            Review funeral homes, cemeteries, and other professional partners applying for elevated access.
          </p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="admin-app-filters">
          {TAB_FILTERS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setFilter(t.id)}
              data-testid={`admin-app-filter-${t.id}`}
              className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold transition border ${
                filter === t.id
                  ? 'bg-[hsl(var(--primary))] text-[#F4E5C2] border-[hsl(var(--primary))]'
                  : 'bg-transparent text-[hsl(var(--primary)/0.7)] border-[hsl(var(--primary))]/25 hover:border-[hsl(var(--primary))]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="ml-card text-center text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : apps.length === 0 ? (
          <div className="ml-card text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-app-empty">
            No applications in this view.
          </div>
        ) : (
          <ul className="space-y-4">
            {apps.map((a) => (
              <li
                key={a.application_id}
                className="ml-card"
                data-testid={`admin-app-row-${a.application_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif text-xl text-[hsl(var(--primary))]">{a.business_name}</h3>
                      <StatusBadge status={a.status} />
                    </div>
                    <div className="mt-1 text-sm text-[hsl(var(--primary)/0.75)] font-medium">
                      {BUSINESS_TYPE_LABEL[a.business_type] || a.business_type} · {a.email}
                      {a.phone ? ` · ${a.phone}` : ''}
                    </div>
                    <dl className="mt-4 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                      <Row label="Legal name" value={a.legal_business_name || '—'} />
                      <Row label="Tax ID" value={a.tax_id_last4 ? `••• ••• ${a.tax_id_last4}` : '—'} />
                      <Row label="Primary contact" value={a.name} />
                      <Row label="Submitted" value={a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'} />
                      {a.approved_at && <Row label="Approved" value={new Date(a.approved_at).toLocaleString()} />}
                      {a.rejected_at && <Row label="Rejected" value={new Date(a.rejected_at).toLocaleString()} />}
                    </dl>
                    {a.rejection_reason && (
                      <div className="mt-3 text-[12px] text-[hsl(var(--destructive))] font-medium">
                        Reason: {a.rejection_reason}
                      </div>
                    )}
                    {approvedPin && approvedPin.id === a.application_id && (
                      <div
                        className="mt-3 inline-flex items-center gap-3 rounded-md border border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.08)] px-3 py-2"
                        data-testid={`admin-app-pin-${a.application_id}`}
                      >
                        <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary))]">PIN</span>
                        <span className="font-mono tracking-[0.3em] text-lg text-[hsl(var(--primary))]">{approvedPin.pin}</span>
                        <span className="text-[11px] text-[hsl(var(--primary)/0.65)] font-medium">Also emailed.</span>
                      </div>
                    )}
                  </div>
                  {a.status === 'pending' && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => approve(a)}
                        className="ml-btn-primary !py-2 text-xs"
                        data-testid={`admin-app-approve-${a.application_id}`}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => reject(a)}
                        className="ml-btn-outline !py-2 text-xs"
                        data-testid={`admin-app-reject-${a.application_id}`}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        </div>
      </AdminLayout>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: 'bg-[hsl(var(--accent)/0.18)]', text: 'text-[hsl(var(--accent))]', label: 'Pending' },
    approved: { bg: 'bg-[rgba(164,195,154,0.22)]', text: 'text-[#557A4C]', label: 'Approved' },
    rejected: { bg: 'bg-[hsl(var(--destructive)/0.12)]', text: 'text-[hsl(var(--destructive))]', label: 'Rejected' },
  }[status] || { bg: 'bg-[hsl(var(--primary)/0.08)]', text: 'text-[hsl(var(--primary)/0.7)]', label: status };
  return (
    <span className={`text-[10px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-full ${map.bg} ${map.text}`}>
      {map.label}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.55)]">{label}</div>
      <div className="font-medium text-[hsl(var(--primary))] text-right truncate">{value}</div>
    </div>
  );
}
